import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBootstrapTheme } from "../../../contexts/BootstrapThemeContext";
import ImageProps from "../../../types/imageStudio/images";
import EffectThemeTabButton from "../../effectTheme/effectThemeButtons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faDownload, faLayerGroup, faObjectGroup, faPlus, faX } from "@fortawesome/free-solid-svg-icons";
import { ContextProps } from "../../../types/imageStudio/contextMenu";
import { ContextMenuLayerSidebarVariations } from "./ContextMenu";
import { EffectThemeVariationsGridProps } from "../../../types/imageStudio/imageLists";

export const EffectThemeVariationsImageGrid = ({
  images,
  width = '100%',
  height = '120px',
  gap = 10,
  className,
  selectedImageIndex = null,
  selectedLayer = null,
  buttonsHeight = "40px",
  buttonsWidth = '50px',
  isBaseImageSelected=false,
  baseImage,
  onImageClick,
  onImageHover,
  onImageHoverEnd,
  onImageSelect,
  onChangeVariationAsLayer,
  onAddVariationAsLayer,
  onCancelSelection,
  onSetVariationOnImage,
  onReplaceBaseImage,
  onDeleteVariation,
  onDownloadAllVariations
}: EffectThemeVariationsGridProps) => {
  const { t } = useTranslation("imageStudio");
  const { effectiveTheme } = useBootstrapTheme();
  const isDark = effectiveTheme === "dark";
  const contextMenuVariations = useRef<HTMLDivElement>(null);
  const [variationIndex, setVariationIndex] = useState<number | null> (null);
  const [contextMenu, setContextMenu] = useState<ContextProps>({
    visible: false,
    x: 0,
    y: 0
  });

  const borderColor = isDark ? "rgba(255,255,255,0.35)" : "rgba(15,23,42,0.25)";
  const shadow = isDark
    ? "0 10px 26px rgba(96,165,250,0.10)"
    : "0 10px 26px rgba(2,6,23,0.08)";

  const openLayerSidebarContextMenu = (
    e: React.MouseEvent,
    index: number
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setVariationIndex(index);
    setContextMenu({
        visible: true,
        x: e.clientX,
        y: e.clientY
    });
  };
  const renderVariationButtons = () => {
    if (
      selectedImageIndex == null ||
      selectedImageIndex < 0 ||
      !images[selectedImageIndex]
    ) {
      return null;
    }

    if (!isBaseImageSelected) {
      return (
        <div
          className="d-flex flex-wrap justify-content-center gap-1 my-1"
          style={{ width: "100%" }}
        >
          <EffectThemeTabButton
            title={t("layer.addAsNewLayer")}
            className="btn btn-sm btn-outline-secondary rounded p-1 m-0"
            onClick={() =>
              onAddVariationAsLayer?.(
                images[selectedImageIndex],
                selectedLayer
              )
            }
            style={{
              minHeight: buttonsHeight,
              width: buttonsWidth
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
          </EffectThemeTabButton>

          <EffectThemeTabButton
            title={t("layer.overwriteLayer")}
            className="btn btn-sm btn-outline-secondary rounded p-1 m-0"
            onClick={() =>
              onChangeVariationAsLayer?.(
                images[selectedImageIndex],
                selectedLayer
              )
            }
            style={{
              minHeight: buttonsHeight,
              width: buttonsWidth
            }}
          >
            <FontAwesomeIcon icon={faCheck} />
          </EffectThemeTabButton>

          <EffectThemeTabButton
            title={t("layer.writeOnBase")}
            className="btn btn-sm btn-outline-secondary rounded p-1 m-0"
            onClick={() =>
              onSetVariationOnImage?.(
                images[selectedImageIndex],
                selectedLayer
              )
            }
            style={{
              minHeight: buttonsHeight,
              width: buttonsWidth
            }}
          >
            <FontAwesomeIcon icon={faObjectGroup} />
          </EffectThemeTabButton>
          <EffectThemeTabButton
            title={t("layer.downloadAllVariations")}
            className="btn btn-sm btn-outline-secondary rounded p-1 m-0"
            onClick={() => onDownloadAllVariations?.(images)}
            style={{
              minHeight: buttonsHeight,
              width: buttonsWidth
            }}
          >
            <FontAwesomeIcon icon={faDownload} />
          </EffectThemeTabButton>
          <EffectThemeTabButton
            title={t("layer.resetVariation")}
            className="btn btn-sm btn-outline-secondary rounded p-1 m-0"
            onClick={() => onCancelSelection?.()}
            style={{
              minHeight: buttonsHeight,
              width: buttonsWidth
            }}
          >
            <FontAwesomeIcon icon={faX} />
          </EffectThemeTabButton>
        </div>
      );
    }

    return (
      <div
        className="d-flex flex-nowrap justify-content-start gap-1 my-1"
        style={{
          width: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          paddingBottom: 2
        }}
      >
        <EffectThemeTabButton
          title={t("layer.replaceBase")}
          className="btn btn-sm btn-outline-secondary rounded p-1 m-0"
          onClick={() => {
            if (
              selectedImageIndex == null ||
              selectedImageIndex < 0
            )
              return;

            onReplaceBaseImage?.(
              images[selectedImageIndex]
            );
          }}
          style={{
            minHeight: buttonsHeight,
            width: buttonsWidth
          }}
        >
          <FontAwesomeIcon icon={faCheck} />
        </EffectThemeTabButton>
        <EffectThemeTabButton
            title={t("layer.downloadAllVariations")}
            className="btn btn-sm btn-outline-secondary rounded p-1 m-0"
            onClick={() => onDownloadAllVariations?.(images)}
            style={{
              minHeight: buttonsHeight,
              width: buttonsWidth
            }}
          >
            <FontAwesomeIcon icon={faDownload} />
          </EffectThemeTabButton>
        <EffectThemeTabButton
          title={t("layer.resetVariation")}
          className="btn btn-sm btn-outline-secondary rounded p-1 m-0"
          onClick={() => onCancelSelection?.()}
          style={{
            minHeight: buttonsHeight,
            width: buttonsWidth
          }}
        >
          <FontAwesomeIcon icon={faX} />
        </EffectThemeTabButton>
      </div>
    );
  };
  const rows: { image: ImageProps; index: number }[][] = [];

  for (let i = 0; i < images.length; i += 2) {
    rows.push([
      { image: images[i], index: i },
      ...(images[i + 1]
        ? [{ image: images[i + 1], index: i + 1 }]
        : [])
    ]);
  };

  useEffect(()=>{
    const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
            e.preventDefault();
            setContextMenu(prev => ({ ...prev, visible: false }));
            return;
        }
    };
    window.addEventListener("keydown", onKey);

    return () => {
        window.removeEventListener("keydown", onKey);
    };
  },[]);

  return (
    <div
      ref={contextMenuVariations}
      className={className}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: `${gap}px`,
        width: "100%",
      }}
    >
      {rows.map((row, rowIndex) => {
        const rowHasSelection =
          selectedImageIndex != null &&
          row.some(item => item.index === selectedImageIndex);

        return (
          <React.Fragment key={rowIndex}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: `${gap}px`,
                width: "100%"
              }}
            >
              {row.map(({ image: img, index }) => {
                const clickable =
                  !!img.onClick ||
                  !!onImageClick ||
                  !!onImageSelect;

                return (
                  <div
                    key={`${img.src}-${index}`}
                    className="m-0 me-1 p-0"
                    style={{
                      minWidth: 0,
                      width: "100%"
                    }}
                    onContextMenu={(e) =>
                      openLayerSidebarContextMenu(
                        e,
                        index
                      )
                    }
                  >
                    <button
                      key={`${img.src}-${index}`}
                      type="button"
                      onClick={() => {
                        img.onClick?.();
                        onImageClick?.(index, img);
                        onImageSelect?.(index, img);
                      }}
                      onMouseEnter={(e) => {
                        onImageHover?.(index, img);
                        if (!clickable) return;
                        (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)";
                      }}
                      onMouseLeave={(e) => {
                        onImageHoverEnd?.(index, img);
                        (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)";
                      }}
                      disabled={!clickable}
                      title={img.title ?? img.alt}
                      style={{
                        all: "unset",
                        cursor: clickable ? "pointer" : "default",
                        width: '100%',//width,
                        height: height,
                        borderRadius: "0.75rem",
                        border: index === selectedImageIndex
                          ? "2px solid #9381FF"
                          : `1px solid ${borderColor}`,
                        overflow: "hidden",
                        boxShadow: shadow,
                        backdropFilter: "blur(8px)",
                        display: "block",
                        position: "relative",
                        opacity: clickable ? 1 : 0.98,
                      }}
                    >
                      <img
                        src={img.src}
                        alt={img.alt ?? `image-${index}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                        draggable={false}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
            {rowHasSelection && renderVariationButtons()}
          </React.Fragment>
        );
      })}
      <ContextMenuLayerSidebarVariations
        wrapperRef={contextMenuVariations}
        contextMenu={contextMenu}
        setContextMenu={setContextMenu}
        variationsIndex={variationIndex}
        variation={
          variationIndex != null && images.length > variationIndex
            ? images[variationIndex]
            : null
        }
        layer={isBaseImageSelected ? null : selectedLayer}
        baseImage={baseImage}
        onAddVariationAsLayer={onAddVariationAsLayer}
        onChangeVariationAsLayer={onChangeVariationAsLayer}
        onSetVariationOnImage={onSetVariationOnImage}
        onReplaceBaseImage={onReplaceBaseImage}
        onDeleteVariation={onDeleteVariation}
        allVariations={images}
        onDownloadAllVariations={onDownloadAllVariations}
      />
    </div>
  );
};

export default EffectThemeVariationsImageGrid;
